/**
 * Gallery of curated datasets.
 *
 * Sticky top bar: free-text search across title/blurb + canonical
 * analysis, plus category chips. State is local-only — we don't bind
 * it to the URL because the gallery is small enough that a deep
 * link to "all + 'iris'" isn't a real use case. Click a card → detail.
 */

import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { ArrowRight, Database, Search, Sparkles } from "lucide-react"
import { CATEGORIES, SAMPLES, type SampleCategory } from "@/lib/samples"

export function Samples() {
  const [query, setQuery] = useState("")
  const [active, setActive] = useState<SampleCategory | "all">("all")

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return SAMPLES.filter(s => {
      if (active !== "all" && s.category !== active) return false
      if (!q) return true
      return (
        s.title.toLowerCase().includes(q) ||
        s.blurb.toLowerCase().includes(q) ||
        s.canonical.toLowerCase().includes(q)
      )
    })
  }, [query, active])

  return (
    <div>
      <header className="mb-8">
        <div className="flex items-baseline gap-3 mb-2">
          <h1
            className="font-display text-4xl tracking-tight text-foreground"
            style={{ fontFamily: '"Space Grotesk", Inter, "Helvetica Neue", sans-serif' }}
          >
            Sample datasets
          </h1>
          <span className="text-sm text-muted-foreground tabular-nums">
            {SAMPLES.length} curated
          </span>
        </div>
        <p className="text-muted-foreground max-w-3xl">
          Each sample ships with a canonical analysis baked into Sparrow's voice — a
          frequencies table, a crosstab, an ANOVA — so you can see the kind of output
          Sparrow produces before you open one in the engine. All files are bundled with
          this page, so the previews work offline.
        </p>
      </header>

      <div className="sticky top-14 z-20 -mx-6 px-6 py-3 mb-8 bg-background/85 backdrop-blur-md border-y border-border">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <label className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or analysis…"
              className="w-full pl-9 pr-3 py-2 rounded-md border border-border bg-card text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-transparent"
            />
          </label>
          <div className="flex flex-wrap items-center gap-1.5">
            <Chip
              label="All"
              active={active === "all"}
              onClick={() => setActive("all")}
            />
            {CATEGORIES.map(c => (
              <Chip
                key={c}
                label={c}
                active={active === c}
                onClick={() => setActive(c)}
              />
            ))}
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-12 text-center text-muted-foreground">
          No samples match that filter. Try a different category or clear the search.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(s => (
            <Link
              key={s.id}
              to={`/samples/${s.id}`}
              className="group p-5 rounded-lg border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all flex flex-col"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground">
                  <Database className="h-3 w-3" />
                  {s.category}
                </span>
                <span className="text-xs text-muted-foreground border border-border rounded-full px-2 py-0.5 tabular-nums">
                  {s.rows.toLocaleString()} × {s.cols}
                </span>
              </div>
              <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                {s.title}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-2 flex-1">
                {s.blurb}
              </p>
              <div className="mt-4 flex items-center justify-between gap-3">
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <Sparkles className="h-3 w-3 text-accent" />
                  {s.canonical}
                </span>
                <span className="text-sm font-medium text-primary inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Open <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

function Chip({
  label, active, onClick,
}: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "px-3 py-1 rounded-full text-xs font-medium transition-colors border",
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-card text-muted-foreground border-border hover:text-foreground hover:bg-secondary/60",
      ].join(" ")}
    >
      {label}
    </button>
  )
}
